import { QueryClient } from 'react-query';

// React Query cache bookkeeping for the Hypercare portal mutations, extracted so the
// coherency rules are unit-testable without rendering hooks (see
// __tests__/hypercareCache.test.ts). The subtlety these encode (PR #654 review): the
// lazily-fetched webhook URL lives under its own per-account key, so invalidating the
// accounts list alone leaves a rotated/deleted URL readable from cache — e.g. "Show URL"
// after a remount would display (and let the user copy) the dead URL until a refetch.

export const hypercareAccountsKey = (organizationId: string) => ['hypercare-accounts', organizationId];
export const hypercareAssignmentsKey = (organizationId: string) => ['hypercare-assignments', organizationId];
export const hypercareWebhookUrlKey = (accountId: string) => ['hypercare-webhook-url', accountId];

/** Mint or rotate: the response IS the new URL — seed the per-account URL cache with it so
 * no stale (now dead) URL can ever be served, even across remounts. */
export const applyHypercareWebhookMintToCache = (
    queryClient: QueryClient,
    accountId: string,
    organizationId: string,
    data: { webhookUrl: string }
): void => {
    queryClient.setQueryData(hypercareWebhookUrlKey(accountId), data);
    queryClient.invalidateQueries(hypercareAccountsKey(organizationId));
};

/** Token revoked: the URL no longer exists server-side (the read endpoint 404s) — drop the
 * cache entry entirely rather than leaving it to be displayed or copied. */
export const applyHypercareWebhookDeleteToCache = (
    queryClient: QueryClient,
    accountId: string,
    organizationId: string
): void => {
    queryClient.removeQueries(hypercareWebhookUrlKey(accountId));
    queryClient.invalidateQueries(hypercareAccountsKey(organizationId));
};

/** Account deleted (the server refuses while devices are still assigned to it): refetch the
 * accounts list, drop the account's URL, and refetch assignments too — another session may
 * have reassigned devices concurrently, and the devices table reads from a separate key. */
export const applyHypercareAccountDeleteToCache = (
    queryClient: QueryClient,
    accountId: string,
    organizationId: string
): void => {
    queryClient.invalidateQueries(hypercareAccountsKey(organizationId));
    queryClient.invalidateQueries(hypercareAssignmentsKey(organizationId));
    queryClient.removeQueries(hypercareWebhookUrlKey(accountId));
};
